import { MIN_DECORATION_SCATTER_RADIUS } from "@/state/useEditorStore";
import type { HexData, MapDecoration, MapNotePinColor } from "@west-marches/shared";
import {
    getCatalogDecorationById,
    getCoastImage,
    getCoastOverlaysForHex,
    getDecorationImage,
    getRoadImage,
    getRoadOverlaysForEdges,
    getTileImage,
    HEX_IMAGE_HEIGHT,
    HEX_IMAGE_WIDTH,
    HEX_SIZE,
    hexToPixel,
    MAP_NOTE_PIN_OVERLAY_ID,
    resolveTileId
} from "@west-marches/shared";

export interface MapNotePinDrawInfo {
  /** Stable id of the underlying map note. Optional for legacy callers. */
  id?: string;
  color: MapNotePinColor;
  offsetX: number;
  offsetY: number;
  hidden?: boolean;
}

function hexCorners(): { x: number; y: number }[] {
  const corners: { x: number; y: number }[] = [];
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 180) * (60 * i);
    corners.push({
      x: HEX_SIZE * Math.cos(angle),
      y: HEX_SIZE * Math.sin(angle),
    });
  }
  return corners;
}

const CORNERS = hexCorners();
const DECORATION_MAX_WIDTH = HEX_IMAGE_WIDTH * 0.34;
const DECORATION_MAX_HEIGHT = HEX_IMAGE_HEIGHT * 0.34;
const PLACEMENT_PREVIEW_OPACITY = 0.45;
const SCATTER_RING_OPACITY = 0.3;
const HIDDEN_MAP_NOTE_PIN_OPACITY = 0.25;
function getPreviewSeed(value: number): number {
  const sine = Math.sin(value * 12.9898) * 43758.5453;
  return sine - Math.floor(sine);
}

export function createScatterPreviewPoints(
  radius: number,
  density: number,
): Array<{ x: number; y: number }> {
  if (radius <= MIN_DECORATION_SCATTER_RADIUS) {
    return [{ x: 0, y: 0 }];
  }

  const points: Array<{ x: number; y: number }> = [{ x: 0, y: 0 }];
  const previewCount = Math.max(2, density + 1);

  for (let index = 0; index < previewCount; index += 1) {
    const angleSeed = getPreviewSeed(index * 17.13 + density * 0.31);
    const distanceSeed = getPreviewSeed(index * 23.71 + density * 0.19);
    const angle = angleSeed * Math.PI * 2;
    const distance = Math.sqrt(distanceSeed) * radius * 0.75;
    points.push({
      x: Math.cos(angle) * distance,
      y: Math.sin(angle) * distance,
    });
  }

  return points;
}

export type PlacementPreview =
  | {
    kind: "decoration";
    decorationId: string;
    x: number;
    y: number;
    radius: number;
    points: Array<{ x: number; y: number }>;
  }
  | {
    kind: "erase";
    x: number;
    y: number;
    radius: number;
  }
  | {
    kind: "map-note-pin";
    color: MapNotePinColor;
    x: number;
    y: number;
    hidden?: boolean;
  };

export function getDecorationDrawDimensions(image: Pick<HTMLImageElement, "naturalWidth" | "naturalHeight">): {
  width: number;
  height: number;
} {
  const sourceWidth = image.naturalWidth > 0 ? image.naturalWidth : HEX_IMAGE_WIDTH;
  const sourceHeight = image.naturalHeight > 0 ? image.naturalHeight : HEX_IMAGE_HEIGHT;
  const scale = Math.min(DECORATION_MAX_WIDTH / sourceWidth, DECORATION_MAX_HEIGHT / sourceHeight, 1);

  return {
    width: sourceWidth * scale,
    height: sourceHeight * scale,
  };
}

const MAP_NOTE_CURSOR_OPACITY = 0.95;

export function drawPlacementPreview(
  ctx: CanvasRenderingContext2D,
  preview: PlacementPreview | null,
): void {
  if (!preview) {
    return;
  }

  if (preview.kind === "map-note-pin") {
    const overlayId = MAP_NOTE_PIN_OVERLAY_ID[preview.color];
    if (!overlayId) return;
    const image = getDecorationImage(overlayId);
    if (!image) return;
    const { width, height } = getDecorationDrawDimensions(image);
    ctx.save();
    ctx.globalAlpha = preview.hidden ? HIDDEN_MAP_NOTE_PIN_OPACITY : MAP_NOTE_CURSOR_OPACITY;
    // Anchor the pin sprite by its tip (bottom-center): the click point lands
    // exactly where the rendered pin will sit after placement.
    ctx.drawImage(
      image,
      preview.x - width / 2,
      preview.y - height,
      width,
      height,
    );
    ctx.restore();
    return;
  }

  ctx.save();
  ctx.globalAlpha = PLACEMENT_PREVIEW_OPACITY;

  ctx.beginPath();
  ctx.globalAlpha = SCATTER_RING_OPACITY;
  ctx.strokeStyle = "rgba(255, 255, 255, 0.95)";
  ctx.lineWidth = 1.5;
  ctx.arc(preview.x, preview.y, preview.radius, 0, Math.PI * 2);
  ctx.stroke();
  ctx.globalAlpha = PLACEMENT_PREVIEW_OPACITY;

  if (preview.kind === "erase") {
    ctx.restore();
    return;
  }

  const image = getDecorationImage(preview.decorationId);
  if (image) {
    const { width, height } = getDecorationDrawDimensions(image);
    for (const point of preview.points) {
      ctx.drawImage(
        image,
        preview.x + point.x - width / 2,
        preview.y + point.y - height / 2,
        width,
        height,
      );
    }
  }

  ctx.restore();
}

type MapNotePinSource = Set<string> | Map<string, MapNotePinDrawInfo> | null | undefined;

function toPinMap(value: MapNotePinSource): Map<string, MapNotePinDrawInfo> {
  if (!value) return new Map();
  if (value instanceof Map) return value;
  // Legacy Set<string> input: render with default pin (white, no offset).
  const map = new Map<string, MapNotePinDrawInfo>();
  for (const key of value) {
    map.set(key, { color: "white", offsetX: 0, offsetY: 0 });
  }
  return map;
}

export function drawHexes(
  ctx: CanvasRenderingContext2D,
  hexes: Record<string, HexData>,
  decorationsOrSortedKeys: Record<string, MapDecoration> | string[],
  sortedKeysOrHoveredKey: string[] | string | null,
  hoveredKeyOrMapNotes?: string | null | MapNotePinSource,
  mapNotesLegacyArg?: MapNotePinSource,
  options?: { dimmedMapNoteId?: string | null },
): void {
  const dimmedMapNoteId = options?.dimmedMapNoteId ?? null;
  const decorations = Array.isArray(decorationsOrSortedKeys) ? {} : decorationsOrSortedKeys;
  const sortedKeys = Array.isArray(decorationsOrSortedKeys)
    ? decorationsOrSortedKeys
    : (sortedKeysOrHoveredKey as string[]);
  const resolvedHoveredKey = Array.isArray(decorationsOrSortedKeys)
    ? (sortedKeysOrHoveredKey as string | null)
    : (typeof hoveredKeyOrMapNotes === "string" || hoveredKeyOrMapNotes === null || hoveredKeyOrMapNotes === undefined
      ? (hoveredKeyOrMapNotes as string | null)
      : null);
  const mapNotesRawSource: MapNotePinSource = Array.isArray(decorationsOrSortedKeys)
    ? (hoveredKeyOrMapNotes && typeof hoveredKeyOrMapNotes === "object" ? (hoveredKeyOrMapNotes as MapNotePinSource) : null)
    : (mapNotesLegacyArg ?? null);
  const resolvedMapNotes = toPinMap(mapNotesRawSource);

  for (const key of sortedKeys) {
    const hex = hexes[key];
    const { x, y } = hexToPixel({ q: hex.q, r: hex.r });

    const tileId = resolveTileId({
      tileId: hex.tileId,
      tileFilename: hex.tileFilename,
      tileSetId: hex.tileSetId,
    });
    const img = tileId ? getTileImage(tileId) : undefined;
    if (img) {
      ctx.drawImage(
        img,
        x - HEX_IMAGE_WIDTH / 2,
        y - HEX_IMAGE_HEIGHT / 2,
        HEX_IMAGE_WIDTH,
        HEX_IMAGE_HEIGHT,
      );
    }

    // Coast overlays for ocean hexes
    const overlays = getCoastOverlaysForHex(hex.q, hex.r, hexes);
    for (const overlay of overlays) {
      const coastImg = getCoastImage(overlay.filename);
      if (!coastImg) continue;

      if (overlay.mirrored) {
        ctx.save();
        ctx.translate(x, y);
        ctx.scale(-1, 1);
        ctx.drawImage(
          coastImg,
          -HEX_IMAGE_WIDTH / 2,
          -HEX_IMAGE_HEIGHT / 2,
          HEX_IMAGE_WIDTH,
          HEX_IMAGE_HEIGHT,
        );
        ctx.restore();
      } else {
        ctx.drawImage(
          coastImg,
          x - HEX_IMAGE_WIDTH / 2,
          y - HEX_IMAGE_HEIGHT / 2,
          HEX_IMAGE_WIDTH,
          HEX_IMAGE_HEIGHT,
        );
      }
    }

    // Road overlays (may composite multiple tiles for edge combos without exact match)
    if (hex.roadEdges && hex.roadEdges.length > 0) {
      const roadInstructions = getRoadOverlaysForEdges(hex.roadEdges, key);
      for (const instr of roadInstructions) {
        const roadImg = getRoadImage(instr.filename);
        if (!roadImg) continue;

        if (instr.rotation === 0 && !instr.mirrored) {
          ctx.drawImage(
            roadImg,
            x - HEX_IMAGE_WIDTH / 2,
            y - HEX_IMAGE_HEIGHT / 2,
            HEX_IMAGE_WIDTH,
            HEX_IMAGE_HEIGHT,
          );
        } else {
          ctx.save();
          ctx.translate(x, y);
          if (instr.mirrored) ctx.scale(-1, 1);
          if (instr.rotation !== 0) ctx.rotate((instr.rotation * Math.PI) / 3);
          ctx.drawImage(
            roadImg,
            -HEX_IMAGE_WIDTH / 2,
            -HEX_IMAGE_HEIGHT / 2,
            HEX_IMAGE_WIDTH,
            HEX_IMAGE_HEIGHT,
          );
          ctx.restore();
        }
      }
    }
  }

  const sortedDecorations = Object.values(decorations).sort((left, right) => {
    const leftCenter = hexToPixel({ q: left.q, r: left.r });
    const rightCenter = hexToPixel({ q: right.q, r: right.r });
    const leftY = leftCenter.y + left.offsetY;
    const rightY = rightCenter.y + right.offsetY;
    if (leftY !== rightY) {
      return leftY - rightY;
    }
    return leftCenter.x + left.offsetX - (rightCenter.x + right.offsetX);
  });

  for (const decoration of sortedDecorations) {
    const image = getDecorationImage(decoration.decorationId);
    if (!image) {
      continue;
    }
    const catalogDecoration = getCatalogDecorationById(decoration.decorationId);
    if (!catalogDecoration) {
      continue;
    }
    const center = hexToPixel({ q: decoration.q, r: decoration.r });
    const { width, height } = getDecorationDrawDimensions(image);
    ctx.drawImage(
      image,
      center.x + decoration.offsetX - width / 2,
      center.y + decoration.offsetY - height / 2,
      width,
      height,
    );
  }

  for (const [key, pin] of resolvedMapNotes) {
    const hex = hexes[key];
    if (!hex) continue;
    if (pin.hidden) continue;
    const overlayId = MAP_NOTE_PIN_OVERLAY_ID[pin.color];
    if (!overlayId) continue;
    const image = getDecorationImage(overlayId);
    if (!image) continue;
    const { x, y } = hexToPixel({ q: hex.q, r: hex.r });
    const { width, height } = getDecorationDrawDimensions(image);
    // Pin sprites anchor by their tip (bottom-center): place the tip at
    // (centerX + offsetX, centerY + offsetY) so dropping a pin lands its tip
    // exactly where the GM clicked.
    ctx.save();
    if (dimmedMapNoteId && pin.id === dimmedMapNoteId) {
      ctx.globalAlpha = 0.4;
    }
    ctx.drawImage(
      image,
      x + pin.offsetX - width / 2,
      y + pin.offsetY - height,
      width,
      height,
    );
    ctx.restore();
  }

  if (resolvedHoveredKey) {
    const hex = hexes[resolvedHoveredKey];
    if (hex) {
      const { x, y } = hexToPixel({ q: hex.q, r: hex.r });
      ctx.beginPath();
      ctx.moveTo(x + CORNERS[0].x, y + CORNERS[0].y);
      for (let i = 1; i < 6; i++) {
        ctx.lineTo(x + CORNERS[i].x, y + CORNERS[i].y);
      }
      ctx.closePath();
      ctx.fillStyle = "rgba(255, 255, 255, 0.1)";
      ctx.fill();
      ctx.strokeStyle = "rgba(255, 255, 255, 0.4)";
      ctx.lineWidth = 2;
      ctx.stroke();
    }
  }
}
